import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@quintoandar-tokko/jopijs';
import PendingCard from '../PendingsCard';
import RadioButtonsGroup from '../../RadioButtonsGroup';
import DashboardEmptyStateCard from '../../DashboardEmptyStateCard';
import QPrecoMarketplaceRow from './QPrecoMarketplaceRow';
import {
  MAP_OPERATIONS,
  PROPERTIES_STATUS,
  PROPERTY_OPERATIONS,
  QPRECO_PRICE_LABEL_VALUES,
} from '../../constants';
import { TRACK_ELEMENT } from '../../constants/track';
import { getPropertiesSearchParams } from '../utils';
import { isQprecoEmpty, mapQprecoLevels } from './utils';
import { setQprecoMarketplace } from '@/store/slices/components/dashboard/DashboardSlice';
import { getQprecoMarketplace } from '@/store/slices/components/dashboard/service';
import useFetchDashboard from '../../customHooks/useFetchDashboard';
import { useTrackPendingTab } from '../../customHooks/useTrackDashboard';
import Trans from '@/components/shared/Trans';
import translations from '../../Dashboard.trans.json';
import PropertyIcon from '@/public/images/dashboard/property.svg';
import styles from './qPrecoMarketplace.module.scss';

function QPrecoMarketplace() {
  const dispatch = useDispatch();
  const track = useTrackPendingTab();
  const {
    loading,
    hasFetched,
    empty,
    error,
    enabled,
    operations,
    selectedOperation,
  } = useSelector((state) => state.DashboardSlice.qprecoMarketplace);
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { data: userData } = useSelector((state) => state.UserSlice);
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();

  const operationId = MAP_OPERATIONS[selectedOperation];
  const selectedOperationData = operations?.[selectedOperation];
  const mappedLevels = mapQprecoLevels(selectedOperationData?.levels);
  const showEmpty = error || empty || !enabled || isQprecoEmpty(mappedLevels);

  useFetchOnViewport(
    (query) => {
      dispatch(getQprecoMarketplace({ query }));
    },
    [],
    { hasFetched }
  );

  const handleOperationChange = (operation) => {
    dispatch(
      setQprecoMarketplace({
        key: 'selectedOperation',
        value: operation.id,
      })
    );
    track.qprecoMarketplace(TRACK_ELEMENT.RADIO_BUTTON_OPTION);
  };

  const handleRowClick = (level) => {
    if (!level?.url) return;
    window.open(level.url, '_blank', 'noopener,noreferrer');
  };

  const priceLabelKey =
    {
      [PROPERTY_OPERATIONS.SALE]: 'sale_price_label',
      [PROPERTY_OPERATIONS.RENT]: 'rent_price_label',
    }[selectedOperation] || null;

  const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
  const fallbackRedirectUrl = `${tokkoBrokerHost}/properties/?search_options=${getPropertiesSearchParams(
    {
      statusId: PROPERTIES_STATUS.AVAILABLE,
      operation_types: [operationId],
      selectedIds,
      ...(priceLabelKey ? { [priceLabelKey]: QPRECO_PRICE_LABEL_VALUES } : {}),
    }
  )}`;
  const headerRedirectUrl = showEmpty
    ? null
    : selectedOperationData?.redirect_url || fallbackRedirectUrl;

  const allowedOptions = [
    { id: PROPERTY_OPERATIONS.SALE, text: Trans(translations, 'Sale') },
    { id: PROPERTY_OPERATIONS.RENT, text: Trans(translations, 'Rent') },
    ...(operations?.[PROPERTY_OPERATIONS.TEMPORARY]
      ? [
          {
            id: PROPERTY_OPERATIONS.TEMPORARY,
            text: Trans(translations, 'Temporary'),
          },
        ]
      : []),
  ];

  return (
    <PendingCard ref={ref} loading={loading || isStale}>
      <PendingCard.Header
        redirectUrl={headerRedirectUrl}
        toolTipText={Trans(translations, 'ShowAll')}
        infoTooltipText={Trans(translations, 'QPrecoMarketplaceTooltip')}
        trackCallback={(element) => track.qprecoMarketplace(element)}
      >
        {Trans(translations, 'QPrecoMarketplace')}
      </PendingCard.Header>
      <Box className={styles.operationBar}>
        <Box className={styles.operationDropdown}>
          <RadioButtonsGroup
            allowedOptions={allowedOptions}
            onClickRadio={handleOperationChange}
            selectedOption={selectedOperation}
          />
        </Box>
      </Box>
      {showEmpty ? (
        <DashboardEmptyStateCard
          icon={PropertyIcon}
          title={Trans(translations, 'HereYouWillSeeQPrecoMarketplace')}
        />
      ) : (
        <Box className={styles.list}>
          {mappedLevels.map((level, index) => (
            <Box key={level.id}>
              <QPrecoMarketplaceRow
                level={level}
                locale={userData?.user_language}
                onClick={handleRowClick}
                trackCallback={(element) => track.qprecoMarketplace(element)}
              />
              {index < mappedLevels.length - 1 && (
                <Box className={styles.separator} />
              )}
            </Box>
          ))}
        </Box>
      )}
    </PendingCard>
  );
}

export default QPrecoMarketplace;
